// src/belediye/utils/sessionRole.js
//
// Amaç:
// - UI kararlarını tek kaynaktan vermek (müdür/personel_1 gibi active-unit rolünü doğru okumak)
// - Multi-unit + activeEffectiveRole (localStorage) desteği
//
// Rol seti (backend):
// ('başkan', 'Başkan'),
// ('birim_personeli', 'Birim Personeli'),
// ('saha_personeli', 'Saha Personeli'),
// ('personel_1', 'Müdür'),
// ('personel_2', 'Halk Masası'),
// ('personel_3', 'Personel 3')
//
// Not:
// - "başkan" genelde global (user.role) gelir.
// - "personel_1" gibi roller çoğunlukla aktif birimdeki membership üzerinden gelir (activeEffectiveRole).

export const ROLES = Object.freeze({
  PRESIDENT: "başkan",
  UNIT_STAFF: "birim_personeli",
  FIELD_STAFF: "saha_personeli",
  SUPERVISOR: "personel_1", // müdür
  DESK_STAFF: "personel_2", // halk masası
  STAFF_3: "personel_3",
});

const normalizeRole = (role) => String(role || "").trim();

/**
 * LocalStorage'dan aktif birim rolünü okumak için güvenli helper.
 * (Bazı ekranlarda store hydrate gecikebilir; localStorage fallback pratik.)
 */
export function getActiveEffectiveRoleFromStorage() {
  try {
    return normalizeRole(window?.localStorage?.getItem("activeEffectiveRole"));
  } catch {
    return "";
  }
}

/**
 * SessionRole = bu oturumda UI'nın esas alacağı rol.
 * Öncelik:
 *  1) activeEffectiveRole (aktif birim seçimine göre)
 *  2) user.role (fallback)
 *
 * @param {object} params
 * @param {object|null} params.user - /common/users/me/ ile gelen user objesi
 * @param {string|null} params.activeEffectiveRole - store/localStorage’dan gelen aktif birim rolü
 * @param {boolean} params.preferStorage - activeEffectiveRole boşsa localStorage fallback kullanılsın mı?
 * @returns {string} sessionRole
 */
export function getSessionRole({
  user,
  activeEffectiveRole,
  preferStorage = true,
} = {}) {
  const uRole = normalizeRole(user?.role);
  const effRole =
    normalizeRole(activeEffectiveRole) ||
    (preferStorage ? getActiveEffectiveRoleFromStorage() : "");

  // Başkan genelde global; fakat bazı projelerde activeEffectiveRole set edilebilir.
  // Burada "effRole varsa onu kullan" kuralını koruyoruz; ancak başkan için fallback garanti.
  return effRole || uRole || "";
}

/**
 * Başkan tespiti: user.role veya superuser.
 * (SessionRole'a bakmıyoruz; başkan global bir yetki gibi davranmalı.)
 */
export function isPresident(user) {
  return Boolean(user?.is_superuser) || normalizeRole(user?.role) === ROLES.PRESIDENT;
}

/**
 * Müdür tespiti: sessionRole personel_1 mi?
 */
export function isSupervisor(sessionRole) {
  return normalizeRole(sessionRole) === ROLES.SUPERVISOR;
}

/**
 * Birim personeli mi? (close approval talep açabilen rol)
 * Not: personel_2 (Halk Masası) ileride farklılaşabilir; şimdilik ayrı tutuluyor.
 */
export function isUnitStaff(sessionRole) {
  return normalizeRole(sessionRole) === ROLES.UNIT_STAFF;
}

/**
 * Saha personeli mi?
 */
export function isFieldStaff(sessionRole) {
  return normalizeRole(sessionRole) === ROLES.FIELD_STAFF;
}

/**
 * Halk Masası mı?
 */
export function isDeskStaff(sessionRole) {
  return normalizeRole(sessionRole) === ROLES.DESK_STAFF;
}

/**
 * Yetki grupları (UI gating için pratik):
 * - close approval karar verebilenler: başkan veya müdür
 */
export function canDecideCloseRequest({ user, sessionRole }) {
  if (isPresident(user)) return true;
  return isSupervisor(sessionRole);
}

/**
 * close approval talep açabilenler:
 * - başkan: hayır
 * - müdür (personel_1): hayır
 * - birim_personeli: evet (asıl talep açan)
 *
 * Not: personel_2/halk masası için şimdilik false bırakıyoruz (netleşince açarsın).
 */
export function canCreateCloseRequest({ user, sessionRole }) {
  if (isPresident(user)) return false;
  if (isSupervisor(sessionRole)) return false;
  return isUnitStaff(sessionRole);
}

/**
 * Debug / log amaçlı: rol label map (UI'da göstermek istersen)
 */
export const ROLE_LABELS = Object.freeze({
  [ROLES.PRESIDENT]: "Başkan",
  [ROLES.UNIT_STAFF]: "Birim Personeli",
  [ROLES.FIELD_STAFF]: "Saha Personeli",
  [ROLES.SUPERVISOR]: "Müdür",
  [ROLES.DESK_STAFF]: "Halk Masası",
  [ROLES.STAFF_3]: "Personel 3",
});